import React, { useState, useContext, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  codeBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  codeText: {
    fontFamily: "monospace",
    fontSize: 42,
    fontWeight: 700,
    letterSpacing: 14,
    color: theme.palette.primary.main,
    background: theme.palette.type === "dark" ? "#1e1e1e" : "#f5f5f5",
    borderRadius: 12,
    padding: "12px 24px",
    border: `2px solid ${theme.palette.primary.main}`,
    userSelect: "all",
  },
  instructions: {
    color: theme.palette.text.secondary,
    textAlign: "center",
    marginTop: theme.spacing(2),
    lineHeight: 1.7,
  },
  step: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  timer: {
    textAlign: "center",
    marginTop: theme.spacing(1),
    color: theme.palette.error.main,
    fontWeight: 600,
  },
}));

const PairingCodeModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [pairingCode, setPairingCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(null);

  // Ouvir evento de conexão estabelecida para fechar o modal
  useEffect(() => {
    if (!whatsAppId || !open) return;
    const companyId = user.companyId;

    const onWhatsappSession = (data) => {
      if (data.action === "update" && data.session.id === whatsAppId) {
        if (data.session.status === "CONNECTED") {
          toast.success("✅ WhatsApp conectado com sucesso!");
          handleClose();
        }
      }
    };

    socket.on(`company-${companyId}-whatsappSession`, onWhatsappSession);
    return () => {
      socket.off(`company-${companyId}-whatsappSession`, onWhatsappSession);
    };
  }, [whatsAppId, open, user.companyId]);

  // Countdown de 60s após receber o código
  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) {
      setPairingCode("");
      setCountdown(null);
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleClose = () => {
    setPhoneNumber("");
    setPhoneError("");
    setPairingCode("");
    setLoading(false);
    setCountdown(null);
    onClose();
  };

  // Formata o número enquanto o usuário digita: +55 (81) 97295-0730
  const formatPhoneDisplay = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 13);
    if (digits.length <= 2) return `+${digits}`;
    if (digits.length <= 4) return `+${digits.slice(0,2)} (${digits.slice(2)}`;
    if (digits.length <= 9) return `+${digits.slice(0,2)} (${digits.slice(2,4)}) ${digits.slice(4)}`;
    return `+${digits.slice(0,2)} (${digits.slice(2,4)}) ${digits.slice(4,9)}-${digits.slice(9)}`;
  };

  const validatePhone = (digits) => {
    if (!digits) return "Informe o número de telefone.";
    if (digits.length < 10) return "Número muito curto. Inclua código do país + DDD.";
    if (digits.length > 13) return "Número muito longo.";
    if (!digits.startsWith("55") && digits.length < 11) return "Para números brasileiros, use 55 + DDD + número.";
    return "";
  };

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 13);
    setPhoneNumber(digits);
    setPhoneError(validatePhone(digits));
  };

  const handleRequestCode = async () => {
    const error = validatePhone(phoneNumber);
    if (error) {
      setPhoneError(error);
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post(
        `/whatsappsession/${whatsAppId}/pairing-code`,
        { phoneNumber }
      );
      setPairingCode(data.code);
      setCountdown(60);
      // Salva que esta conexão usou pairing code
      try {
        const stored = JSON.parse(localStorage.getItem("pairingCodeConnections") || "[]");
        if (!stored.includes(whatsAppId)) {
          stored.push(whatsAppId);
          localStorage.setItem("pairingCodeConnections", JSON.stringify(stored));
        }
      } catch (_) {}
      toast.info("🔑 Código gerado! Digite-o no seu WhatsApp em até 60 segundos.");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        📱 Conectar via Código de Pareamento
      </DialogTitle>
      <DialogContent>
        {!pairingCode ? (
          <>
            <Typography variant="body2" className={classes.instructions}>
              Informe o número do WhatsApp com <span className={classes.step}>código do país + DDD</span>.<br />
              Exemplo: <strong>5511999998888</strong>
            </Typography>
            <Box mt={2}>
              <TextField
                label="Número do WhatsApp"
                placeholder="+55 (11) 99999-8888"
                fullWidth
                variant="outlined"
                value={formatPhoneDisplay(phoneNumber)}
                onChange={handlePhoneChange}
                disabled={loading}
                error={!!phoneError}
                helperText={phoneError || `${phoneNumber.length}/13 dígitos — código do país + DDD + número`}
                inputProps={{ maxLength: 20 }}
              />
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2" className={classes.instructions}>
              No seu WhatsApp, vá em:<br />
              <span className={classes.step}>Configurações → Dispositivos Vinculados → Vincular um dispositivo</span>
              <br /><br />
              Toque em <strong>"Vincular com número de telefone"</strong> e insira o código:
            </Typography>

            <div className={classes.codeBox}>
              <span className={classes.codeText}>{pairingCode}</span>
            </div>

            {countdown !== null && (
              <Typography variant="body2" className={classes.timer}>
                ⏱ Código expira em {countdown}s
              </Typography>
            )}

            <Typography variant="caption" className={classes.instructions}>
              Aguardando conexão... O modal fechará automaticamente ao conectar.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="default">
          Cancelar
        </Button>
        {!pairingCode && (
          <Button
            onClick={handleRequestCode}
            color="primary"
            variant="contained"
            disabled={loading || !phoneNumber || !!phoneError}
          >
            {loading ? <CircularProgress size={20} /> : "Gerar Código"}
          </Button>
        )}
        {pairingCode && (
          <Button
            onClick={() => { setPairingCode(""); setCountdown(null); }}
            color="primary"
            variant="outlined"
          >
            Tentar Novamente
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PairingCodeModal;
